import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {Injectable} from "@angular/core";
import {UpdateCredentialsData} from "@app/core/credentials/domain/update-credentials-data";
import {SecureStorageLocal} from "@app/core/shared/domain/storage/secure-storage.local";
import {JSONMapper} from "@app/core/shared/infrastructure/mappers/json.mapper";

const CREDENTIALS_STORAGE_KEY = 'credentials';

@Injectable({
    providedIn: "root",
})
export class CredentialsLocalStorage extends CredentialsStorage{
    private readonly mapper = new JSONMapper<{ email: string, password: string }>();
    
    
    //Used when the user is not logged in.
    private temporalCredentials: Credentials | null = null;
    
    constructor(private readonly storage: SecureStorageLocal) {
        super();
    }

    public save(credentials: Credentials): Promise<void> {
        // If we have temporal credentials, we delete them because the definitive credentials are saved.
        this.temporalCredentials = null;
        
        return this.storage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(credentials));
    }

    public async getCredentials(): Promise<Credentials | null> {
        const credentials = await this.storage.getItem(CREDENTIALS_STORAGE_KEY);
        if (credentials) {
            const cred = this.mapper.deserialize(credentials);
            return new Credentials(cred.email, cred.password);
        }
        return null;
    }

    /**
     * It returns the temporal credentials if they exist.
     * The temporal credentials exist between the moment the user enters the email and the password and the moment the user logs in.
     */
    getTemporalCredentials(): Credentials | null {
        return this.temporalCredentials;
    }

    async update(updateCredentialsData: UpdateCredentialsData): Promise<void> {
        const credentials = await this.getCredentials();
        if(credentials) {
            return this.save(Object.assign(credentials,updateCredentialsData) as Credentials);
        }
    }

    /**
     * It saves the user and the password in the storage temporarily.
     * The temporal credentials will be deleted when the user logged in or when the app is closed.
     * @param credentials
     */
    saveTemporalCredentials(credentials: Credentials): void {
        this.temporalCredentials = credentials;
    }

    public deleteCredentials(): Promise<void> {
        return this.storage.removeItem(CREDENTIALS_STORAGE_KEY);
    }
}
